import { useEffect } from 'react';
import { useFeatureServices } from '../context/ofertas-context';
import { RegisteredDisclaimer } from './get-disclaimer';

export interface RegisteredDisclaimers {
	[key: string]: RegisteredDisclaimer;
}

export interface NamedDisclaimer {
	readonly ref: string;
	readonly text: string;
}

export type DisclaimerType = string | NamedDisclaimer;

export const getDisclaimerText = (
	disclaimer: DisclaimerType,
): string | undefined => {
	return typeof disclaimer === 'string' ? disclaimer : disclaimer?.text;
};

export function isStandardReferenceDisclaimer(
	disclaimer: DisclaimerType,
): disclaimer is string {
	return typeof disclaimer === 'string';
}

export const useDisclaimerReference = (
	disclaimers: RegisteredDisclaimer[] | undefined,
): void => {
	const disclaimerManager =
		useFeatureServices()['disclaimer-manager'].getScopedDisclaimerManager();

	const registerDisclaimer = (
		disclaimer: DisclaimerType,
		registeredDisclaimersMap: RegisteredDisclaimers,
	) => {
		const text = getDisclaimerText(disclaimer);

		if (!text) {
			return;
		}

		if (isStandardReferenceDisclaimer(disclaimer)) {
			const reference = disclaimerManager.registerReferenceDisclaimer(
				'footer-reference',
				text,
			);

			registeredDisclaimersMap[text] = {
				text: disclaimer,
				ref: reference,
			};
		} else {
			if (disclaimer.ref) {
				const reference = disclaimerManager.registerReferenceDisclaimer(
					'footer-reference',
					text,
					disclaimer.ref,
				);

				registeredDisclaimersMap[text] = {
					text: disclaimer.text,
					ref: reference,
				};
			} else {
				disclaimerManager.registerGlobalDisclaimer(text);

				registeredDisclaimersMap[text] = {
					text: disclaimer.text,
					ref: undefined,
				};
			}
		}
	};

	const handlePropsChange = (
		registeredDisclaimersMap: RegisteredDisclaimers,
	): void => {
		if (disclaimers) {
			disclaimers.forEach((disclaimer) =>
				registerDisclaimer(disclaimer, registeredDisclaimersMap),
			);
		}
	};

	useEffect(() => {
		const registeredDisclaimersMap: RegisteredDisclaimers = {};

		handlePropsChange(registeredDisclaimersMap);
	}, [disclaimers]);
};
